/**
 * PatientChart — Three-Panel Console
 *
 * Workstream A — The signature UX change.
 *
 * Replaces the tab-based PatientDetail.tsx with a three-panel layout:
 *   Left  (240px fixed)  — Patient context, allergies, meds, quick actions
 *   Center (flex)         — Scrollable clinical sections (CDS → Overview → Labs → …)
 *   Right (360px toggle)  — IntelliHealth Agent chat panel
 *
 * All data from existing services. No new API endpoints. Same role-gating.
 *
 * PRD Reference: Section 4C — Three-Panel Console
 */

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ArrowLeft,
  Edit,
  Plus,
  AlertTriangle,
  XCircle,
  CheckCircle,
  Calendar,
  Mail,
  Phone,
  Pill,
  FlaskConical,
  MessageSquare,
  DollarSign,
  MoreVertical,
  LayoutGrid,
  FileText,
  Package,
  ClipboardList,
  Clock,
  Stethoscope,
  Receipt,
  Files,
  Sparkles,
  Heart,
  User,
  Shield,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// ── Patient tab components (existing) ──
import PatientOverviewTab from '@/components/patient/PatientOverviewTab';
import PatientSOAPNotesTab from '@/components/patient/PatientSOAPNotesTab';
import PatientPrescriptionsTab from '@/components/patient/PatientPrescriptionsTab';
import PatientLabsTab from '@/components/patient/PatientLabsTab';
import PatientDocumentsTab from '@/components/patient/PatientDocumentsTab';
import PatientConsentsTab from '@/components/patient/PatientConsentsTab';
import PatientAppointmentsTab, {
  patientAppointmentsQueryKey,
} from '@/components/patient/PatientAppointmentsTab';
import PatientInvoicesTab from '@/components/patient/PatientInvoicesTab';
import PatientShipmentsTab from '@/components/patient/PatientShipmentsTab';
import PatientGuidedOrdersTab from '@/components/patient/PatientGuidedOrdersTab';
import PatientIntakesTab from '@/components/patient/PatientIntakesTab';
import PatientTimelineTab from '@/components/patient/PatientTimelineTab';
import PatientMessagingTab from '@/components/patient/PatientMessagingTab';
import PatientMdiCasesTab from '@/components/patient/PatientMdiCasesTab';
// PatientIntelliHealthTab was removed in feat/ehr-encounter-convergence (#798).
// Replaced inline below with the new canonical IntelliHealthSection.
import IntelliHealthSection from '@/components/patient/intellihealth/IntelliHealthSection';
import { TaskPanel } from '@/components/tasks/TaskPanel';

// ── Three-panel specific components ──
import CollapsibleSection from '@/components/patient/CollapsibleSection';
import IntelliHealthRightPanel from '@/components/intellihealth/IntelliHealthRightPanel';
import CDSTriagePanel from '@/components/intellihealth/CDSTriagePanel';
import CDSCardStrip from '@/components/intellihealth/CDSCardStrip';
import TrafficLightBanner, { inferVerdict } from '@/components/clinic/intellihealth/TrafficLightBanner';
import ActiveMedicationsGrid from '@/components/patient/ActiveMedicationsGrid';
import VitalsStrip, { VitalMetric } from '@/components/patient/VitalsStrip';
import ConditionChips from '@/components/patient/ConditionChips';

// ── Modals / sheets (existing) ──
import PatientFormDrawer from '@/components/clinic/PatientFormDrawer';
import PrescriptionFormSheet from '@/components/clinic/PrescriptionFormSheet';
import GuidedOrderPickerDialog from '@/components/clinic/GuidedOrderPickerDialog';
import AppointmentWizard from '@/components/emr/AppointmentWizard';
import NoteFormDrawer from '@/components/clinic/NoteFormDrawer';
import UpdatePatientStatusDialog from '@/components/patient/UpdatePatientStatusDialog';
import BillPatientDialog from '@/components/billing/BillPatientDialog';
import SOAPNotesListSheet from '@/components/patient/SOAPNotesListSheet';

// ── Services and utilities ──
import { patientService } from '@/services/patientService';
import { mdiService } from '@/services/mdiService';
import { emrService, EmrClinicalNote } from '@/services/emrService';
import { getPatientIntakeStatus } from '@/services/intakeService';
import { Patient, PaginationParams } from '@/lib/types';
import { useAuth } from '@/contexts/AuthContext';
import { useClinic } from '@/contexts/ClinicContext';
import { permissionCheck } from '@/lib/permission-check';
import { cn, getDefaultContactValue, toTitleCase } from '@/lib/utils';
import { useBreadcrumbLabel } from '@/components/ui/breadcrumb';
import { formatDOB, calculateAge } from '@/lib/dateUtils';
import { toast } from '@/hooks/use-toast';
import '@/prescription.css';

// ============================================================================
// Component
// ============================================================================

const PatientChart: React.FC = () => {
  const { user } = useAuth();
  const { clinic, isAdmin, isPractitioner, isPractitionerStaff, rolePermissions } = useClinic();
  const isGuidedClinic = clinic?.clinic_archetype === 'guided';
  const isHybridClinic = clinic?.clinic_archetype === 'hybrid';
  const showIntakesTab = !isGuidedClinic;
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { setLabel: setBreadcrumbLabel } = useBreadcrumbLabel();

  // ── State ──
  const [patient, setPatient] = useState<Patient | null>(null);
  const isExternalPatient = !!(patient?.clinic_id && clinic?.id && patient.clinic_id !== clinic.id);
  const [isLoading, setIsLoading] = useState(true);
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);

  // Modals
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [isNoteDrawerOpen, setIsNoteDrawerOpen] = useState(false);
  const [isPrescriptionDrawerOpen, setIsPrescriptionDrawerOpen] = useState(false);
  const [isGuidedPickerOpen, setIsGuidedPickerOpen] = useState(false);
  const [pendingGuidedOrderId, setPendingGuidedOrderId] = useState<string | null>(null);
  const [isAppointmentSheetOpen, setIsAppointmentSheetOpen] = useState(false);
  const [isBillPatientDialogOpen, setIsBillPatientDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<'active' | 'inactive'>('active');
  const [soapNotesSheetOpen, setSoapNotesSheetOpen] = useState(false);

  // SOAP notes
  const [recentSoapNotes, setRecentSoapNotes] = useState<EmrClinicalNote[]>([]);
  const [isLoadingSoapNotes, setIsLoadingSoapNotes] = useState(true);
  const [soapNoteParams, setSoapNoteParams] = useState<PaginationParams>({
    page: 1,
    limit: 5,
    sortBy: 'created_at',
    sortOrder: 'DESC',
  });

  // Other
  const [isMdiEnabled, setIsMdiEnabled] = useState(false);
  const [intakeCount, setIntakeCount] = useState(0);
  const [recentShipments] = useState([]);
  const canViewTasksTab = Boolean(user?.practitionerId) && (isPractitioner || isAdmin);

  // Auto-collapse right panel below 1280px (PRD requirement)
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 1279px)');
    const handler = (e: MediaQueryListEvent | MediaQueryList) => {
      setRightPanelCollapsed(e.matches);
    };
    // Set initial state
    handler(mql);
    // Listen for threshold crossings
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // ── Data fetching ──

  const fetchPatient = useCallback(async (patientId: string) => {
    setIsLoading(true);
    try {
      const response = await patientService.getPatientById(patientId);
      setPatient(response.data);
    } catch (error) {
      console.error('Error fetching patient:', error);
      navigate(-1);
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  // Reads SOAP-typed clinical notes from the new emr_clinical_notes model.
  // The legacy soap_notes table was retired by migration 20260423000003 (rolled
  // into emr_clinical_notes). The encounter convergence work moved data — but
  // not UX — so the three-panel console keeps showing SOAP notes from the
  // canonical EmrClinicalNote source via emrService.
  const fetchSoapNotes = useCallback(async (patientId: string) => {
    if (!clinic?.id) return;
    setIsLoadingSoapNotes(true);
    try {
      const response = await emrService.getPatientNotesPaginated(clinic.id, patientId, {
        page: soapNoteParams.page,
        limit: soapNoteParams.limit,
        noteType: 'soap',
      });
      setRecentSoapNotes(response.data);
      setSoapNoteParams((prev) => ({ ...prev, ...response.pagination }));
    } catch (error) {
      console.error('Error fetching SOAP notes:', error);
    } finally {
      setIsLoadingSoapNotes(false);
    }
  }, [clinic?.id, soapNoteParams.page, soapNoteParams.limit]);

  useEffect(() => { if (id) fetchPatient(id); }, [id, fetchPatient]);
  useEffect(() => { if (patient?.id) fetchSoapNotes(patient.id); }, [patient?.id, soapNoteParams.page]);

  useEffect(() => {
    setBreadcrumbLabel(patient?.full_name ? toTitleCase(patient.full_name) : null);
    return () => setBreadcrumbLabel(null);
  }, [patient?.full_name, setBreadcrumbLabel]);

  useEffect(() => {
    if (patient?.id) {
      getPatientIntakeStatus(patient.id).then((res) => {
        if (res.success && res.data) {
          const total = (res.data.pending?.length || 0) + (res.data.completed?.length || 0) + (res.data.reviewed?.length || 0);
          setIntakeCount(total);
        }
      }).catch(() => {});
    }
  }, [patient?.id]);

  useEffect(() => {
    if (clinic?.id) {
      mdiService.isEnabled(clinic.id).then(setIsMdiEnabled).catch(() => setIsMdiEnabled(false));
    }
  }, [clinic?.id]);

  // ── Handlers ──

  const handleEditPatient = async (data: { success?: boolean }) => {
    if (data?.success && patient?.id) {
      toast({ title: 'Success', description: 'Patient updated successfully', variant: 'success' });
      await fetchPatient(patient.id);
      setIsEditDrawerOpen(false);
    }
  };

  const handleCreateNote = async () => {
    if (patient?.id) await fetchSoapNotes(patient.id);
  };

  const handleStatusChange = (newStatus: 'active' | 'inactive') => {
    setPendingStatus(newStatus);
    setStatusDialogOpen(true);
  };

  const handleStatusUpdateSuccess = async () => {
    if (patient?.id) {
      await fetchPatient(patient.id);
      await fetchSoapNotes(patient.id);
    }
  };

  // ── Derived ──

  const getDefaultContact = (key: 'email' | 'phone') => {
    const contacts = patient?.contacts?.filter((c: { key: string; value?: string; is_default?: boolean }) => c.key === key) || [];
    const defaultContact = contacts.find((c: { is_default?: boolean }) => c.is_default);
    const fallback = contacts[0]?.value;
    const patientField = key === 'email' ? patient?.email : patient?.phone;
    return defaultContact?.value || fallback || patientField || null;
  };

  const defaultEmail = patient ? getDefaultContact('email') : null;
  const defaultPhone = patient ? getDefaultContact('phone') : null;

  const getInitials = (name: string) =>
    name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

  const statusConfig = patient
    ? patient.status === 'active'
      ? { label: 'Active', className: 'bg-emerald-600 text-white border-0', icon: <CheckCircle className="h-3 w-3" /> }
      : { label: 'Inactive', className: 'bg-slate-500 text-white border-0', icon: <XCircle className="h-3 w-3" /> }
    : null;

  // Traffic light from latest SOAP
  const verdict = useMemo(() => {
    if (recentSoapNotes.length === 0) return null;
    const latest = recentSoapNotes[0];
    return inferVerdict(latest.assessment, latest.plan, null);
  }, [recentSoapNotes]);

  // ── Derived vitals for VitalsStrip (Cumulus spec: under patient header) ──
  const vitalsMetrics: VitalMetric[] = useMemo(() => {
    if (!patient) return [];
    const metrics: VitalMetric[] = [];
    if (patient.weight) {
      metrics.push({
        label: 'Weight',
        value: patient.weight,
        unit: patient.weight_unit || 'lbs',
        rangeStatus: 'unknown',
      });
    }
    if (patient.height) {
      metrics.push({
        label: 'Height',
        value: patient.height,
        unit: patient.height_unit || 'in',
        rangeStatus: 'unknown',
      });
    }
    if (patient.weight && patient.height) {
      const weightKg = patient.weight_unit === 'kg' ? Number(patient.weight) : Number(patient.weight) * 0.453592;
      const heightM = patient.height_unit === 'cm' ? Number(patient.height) / 100 : Number(patient.height) * 0.0254;
      if (heightM > 0) {
        const bmi = (weightKg / (heightM * heightM)).toFixed(1);
        const bmiNum = parseFloat(bmi);
        metrics.push({
          label: 'BMI',
          value: bmi,
          rangeStatus: bmiNum < 18.5 ? 'below' : bmiNum > 30 ? 'above' : bmiNum > 25 ? 'above' : 'in-range',
          rangeLabel: bmiNum < 18.5 ? 'Underweight' : bmiNum > 30 ? 'Obese' : bmiNum > 25 ? 'Overweight' : 'Normal',
        });
      }
    }
    return metrics;
  }, [patient]);

  // ── Loading skeleton ──

  if (isLoading) {
    return (
      <div className="flex h-[calc(100vh-64px)]">
        <div className="w-60 border-r border-slate-200 p-4 space-y-4">
          <Skeleton className="h-16 w-16 rounded-xl mx-auto" />
          <Skeleton className="h-5 w-32 mx-auto" />
          <Skeleton className="h-4 w-24 mx-auto" />
          <div className="space-y-2 pt-4">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-8 w-full" />)}
          </div>
        </div>
        <div className="flex-1 p-6 space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-40 w-full rounded-lg" />
          <Skeleton className="h-40 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  // Not found
  if (!patient) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <XCircle className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-600 mb-4">Patient not found</p>
          <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Go Back
          </Button>
        </div>
      </div>
    );
  }


  // ============================================================================
  // Render — Three-Panel Layout
  // ============================================================================

  return (
    <div className="flex h-[calc(100vh-64px)] overflow-hidden -mx-6 -mt-6">
      {/* ═══════════════════════════════════════════════════
           LEFT PANEL — Patient Context (240px fixed)
         ═══════════════════════════════════════════════════ */}
      <aside className="w-60 flex-shrink-0 border-r border-slate-200 bg-white overflow-y-auto">
        <div className="p-4 space-y-4">
          {/* Back button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-slate-500 hover:text-slate-700 -ml-2"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>

          {/* Patient identity */}
          <div className="text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-xl bg-teal-100">
              <span className="text-lg font-semibold text-teal-700">
                {getInitials(patient.full_name)}
              </span>
            </div>
            <h2 className="mt-2 text-sm font-semibold text-slate-900">
              {toTitleCase(patient.full_name)}
            </h2>
            {statusConfig && (
              <Badge className={cn('mt-1 text-[10px]', statusConfig.className)}>
                <span className="flex items-center gap-1">{statusConfig.icon}{statusConfig.label}</span>
              </Badge>
            )}
            {patient.dob && (
              <p className="mt-1 text-xs text-slate-500">
                {formatDOB(patient.dob)}
                {calculateAge(patient.dob) !== null && ` (${calculateAge(patient.dob)} yrs)`}
              </p>
            )}
            {patient.pmk_patient_id && (
              <p className="text-[10px] font-mono text-slate-400 mt-0.5">
                ID: {patient.pmk_patient_id}
              </p>
            )}
            {patient.weight && (
              <p className="mt-1 text-xs text-slate-500">
                {patient.weight} {patient.weight_unit || 'lbs'}
                {patient.height && ` · ${patient.height} ${patient.height_unit || 'in'}`}
              </p>
            )}
          </div>

          {/* Contact */}
          <div className="space-y-1 text-xs">
            {defaultEmail && (
              <div className="flex items-center gap-2 text-slate-600 truncate">
                <Mail className="h-3 w-3 text-slate-400 flex-shrink-0" />
                <span className="truncate">{defaultEmail}</span>
              </div>
            )}
            {defaultPhone && (
              <div className="flex items-center gap-2 text-slate-600">
                <Phone className="h-3 w-3 text-slate-400 flex-shrink-0" />
                {defaultPhone}
              </div>
            )}
          </div>

          {/* Traffic Light */}
          {verdict && (
            <div className="pt-1">
              <TrafficLightBanner verdict={verdict} />
            </div>
          )}

          {/* Allergies */}
          {patient.allergies && patient.allergies.toLowerCase() !== 'none' && patient.allergies.toLowerCase() !== 'nkda' && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-2.5">
              <div className="flex items-center gap-1.5 mb-1">
                <AlertTriangle className="h-3 w-3 text-red-500" />
                <span className="text-[10px] font-semibold uppercase text-red-600">Allergies</span>
              </div>
              <p className="text-xs text-red-700">{patient.allergies}</p>
            </div>
          )}

          {/* Quick Actions */}
          <div className="space-y-1.5 pt-2 border-t border-slate-100">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">
              Quick Actions
            </p>
            {!isGuidedClinic && (isPractitioner || isPractitionerStaff || isAdmin) && (
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start text-xs h-8 border-slate-200"
                onClick={() => setIsPrescriptionDrawerOpen(true)}
              >
                <Pill className="h-3 w-3 mr-2 text-teal-500" /> Prescribe
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start text-xs h-8 border-slate-200"
              onClick={() => navigate(`${location.pathname}?tab=labs`)}
            >
              <FlaskConical className="h-3 w-3 mr-2 text-blue-500" /> Order Labs
            </Button>
            {!isGuidedClinic && (
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start text-xs h-8 border-slate-200"
                onClick={() => setIsAppointmentSheetOpen(true)}
              >
                <Calendar className="h-3 w-3 mr-2 text-violet-500" /> Schedule
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start text-xs h-8 border-slate-200"
              onClick={() => setIsBillPatientDialogOpen(true)}
            >
              <DollarSign className="h-3 w-3 mr-2 text-emerald-500" /> Bill
            </Button>

            {/* More actions */}
            {permissionCheck('patients:update', rolePermissions, isPractitioner || isPractitionerStaff || isAdmin) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="w-full justify-start text-xs h-8 text-slate-500">
                    <MoreVertical className="h-3 w-3 mr-2" /> More
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onClick={() => setIsEditDrawerOpen(true)}>
                    <Edit className="mr-2 h-4 w-4" /> Edit Patient
                  </DropdownMenuItem>
                  {!isGuidedClinic && permissionCheck('soap_notes:create', rolePermissions, isPractitioner || isAdmin) && (
                    <DropdownMenuItem onClick={() => setIsNoteDrawerOpen(true)}>
                      <FileText className="mr-2 h-4 w-4" /> Add SOAP Note
                    </DropdownMenuItem>
                  )}
                  {patient.status === 'active' ? (
                    <DropdownMenuItem onClick={() => handleStatusChange('inactive')}>
                      <XCircle className="mr-2 h-4 w-4" /> Deactivate
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onClick={() => handleStatusChange('active')}>
                      <CheckCircle className="mr-2 h-4 w-4" /> Activate
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </aside>

      {/* ═══════════════════════════════════════════════════
           CENTER PANEL — Clinical Sections (scrollable)
         ═══════════════════════════════════════════════════ */}
      <main className="flex-1 overflow-y-auto p-5 space-y-3">
        {/* ═══ CDS CARD STRIP (Cumulus Spec 7A) ═══
          Horizontal card strip at top of center panel.
          72px cards, severity-coded left borders, max 3 visible.
          Full CDSTriagePanel stays in right rail for deep dive.
        ═══════════════════════════════════════════════════ */}
        {/* CDS Card Strip — horizontal layout per Cumulus spec 7A
            Max 3 visible, 72px cards, severity borders (red/amber/blue),
            "Show N more" overflow. ACTION+WATCH cards only in center;
            INFO-level context stays in right rail CDSTriagePanel. */}
        <CDSCardStrip
          patientId={patient.id}
          patientName={patient.full_name || null}
          medicationAllergies={patient.medication_allergies ?? null}
          onPrescribe={() => setIsPrescriptionDrawerOpen(true)}
          onOrderLab={() => navigate(`${location.pathname}?tab=labs`)}
          onSchedule={() => setIsAppointmentSheetOpen(true)}
        />

        {/* Vitals Strip + Condition Chips — Cumulus spec item 1: clinical context at top */}
        <VitalsStrip metrics={vitalsMetrics} className="mb-1" />
        <ConditionChips
          conditions={(patient as any).conditions?.map((c: any) => typeof c === 'string' ? c : c?.name) ?? []}
          allergies={patient.allergies}
          className="mb-1"
        />

        {/* ═══ CLINICAL-FIRST SECTION ORDER (Cumulus spec item 1) ═══
             CDS Strip → Vitals → Meds → Labs → SOAP → Overview → [admin collapsed]
         ═══════════════════════════════════════════════════════════ */}

        {/* Section 1: Medications — expanded for clinicians (Cumulus spec 7B role-based) */}
        <CollapsibleSection
          title="Medications & Prescriptions"
          icon={<Pill className="h-4 w-4 text-teal-500" />}
          defaultExpanded={isPractitioner || isPractitionerStaff}
        >
          <div className="space-y-3">
            <ActiveMedicationsGrid
              patientId={patient.id}
              onPrescribe={() => setIsPrescriptionDrawerOpen(true)}
            />
            <PatientPrescriptionsTab patientId={patient.id} />
          </div>
        </CollapsibleSection>

        {/* Section 2: Labs — expanded for clinicians (Cumulus spec 7B role-based) */}
        <CollapsibleSection
          title="Labs & Results"
          icon={<FlaskConical className="h-4 w-4 text-blue-500" />}
          defaultExpanded={isPractitioner || isPractitionerStaff}
        >
          <PatientLabsTab
            patientId={patient.id}
            patientName={patient.full_name || ''}
            patientEmail={patient.email || getDefaultContactValue(patient.contacts, 'email')}
            patientPhone={patient.phone || getDefaultContactValue(patient.contacts, 'phone')}
            patientAddress={{
              street: patient.addresses?.[0]?.line_1,
              city: patient.addresses?.[0]?.city,
              state: patient.addresses?.[0]?.stateInfo?.code,
              zip: patient.addresses?.[0]?.postal_code,
            }}
            patientDob={patient.dob}
            patientGender={(() => {
              const raw = (patient.gender || patient.sex || '').toLowerCase();
              if (raw.startsWith('m')) return 'male';
              if (raw.startsWith('f')) return 'female';
              return 'unknown';
            })()}
          />
        </CollapsibleSection>

        {/* Section 3: Clinical Notes (SOAP) */}
        {!isGuidedClinic && permissionCheck('soap_notes:view', rolePermissions, isPractitioner || isPractitionerStaff || isAdmin) && (
          <CollapsibleSection
            title="Clinical Notes (SOAP)"
            icon={<FileText className="h-4 w-4 text-slate-500" />}
            defaultExpanded={isPractitioner || isPractitionerStaff}
          >
            <PatientSOAPNotesTab
              isLoading={isLoadingSoapNotes}
              notes={recentSoapNotes}
              pagination={soapNoteParams as PaginationParams}
              onPaginationChange={(page) => setSoapNoteParams((prev) => ({ ...prev, page: page as number }))}
              refetch={async () => { if (patient?.id) await fetchSoapNotes(patient.id); }}
            />
          </CollapsibleSection>
        )}

        {/* ── Collapsed by default ── */}

        

        {/* Section 4: Overview */}
        <CollapsibleSection
          title="Overview"
          icon={<LayoutGrid className="h-4 w-4 text-slate-500" />}
          
        >
          <PatientOverviewTab patient={patient} onPatientUpdated={() => id && fetchPatient(id)} />
        </CollapsibleSection>

        {/* Section 5: Guided Orders */}
        {(isGuidedClinic || isHybridClinic || isExternalPatient) && (
          <CollapsibleSection
            title="Guided Orders"
            icon={<Package className="h-4 w-4 text-slate-500" />}
          >
            <PatientGuidedOrdersTab
              patientId={patient.id}
              clinicId={isExternalPatient ? patient.clinic_id : clinic?.id}
              fulfillmentSource={isExternalPatient ? 'nimbus' : undefined}
              patient={patient}
            />
          </CollapsibleSection>
        )}

        {/* Section 6: Intakes */}
        {showIntakesTab && (
          <CollapsibleSection
            title="Intake History"
            icon={<ClipboardList className="h-4 w-4 text-slate-500" />}
            summary={intakeCount > 0 ? `${intakeCount} intake${intakeCount !== 1 ? 's' : ''}` : undefined}
          >
            <PatientIntakesTab
              patientId={patient.id}
              patientDbId={patient.id}
              patientEmail={patient.email || getDefaultContactValue(patient.contacts, 'email')}
              patientName={patient.full_name || ''}
              patientGender={patient.gender || patient.sex}
              mode="intakes"
            />
          </CollapsibleSection>
        )}

        {/* Section 7: Documents & Consents */}
        <CollapsibleSection
          title="Documents & Consents"
          icon={<Files className="h-4 w-4 text-slate-500" />}
        >
          <div className="space-y-4">
            <PatientDocumentsTab patientId={patient.id} />
            {!isGuidedClinic && (
              <>
                <hr className="border-slate-100" />
                <PatientConsentsTab
                  patientId={patient.id}
                  patientName={patient.full_name || `${patient.first_name} ${patient.last_name}`}
                />
              </>
            )}
          </div>
        </CollapsibleSection>

        {/* Section 8: Appointments */}
        {!isGuidedClinic && (
          <CollapsibleSection
            title="Appointments"
            icon={<Calendar className="h-4 w-4 text-slate-500" />}
          >
            <PatientAppointmentsTab patientId={patient.id} />
          </CollapsibleSection>
        )}

        {/* Section 9: Billing — expanded for admins (Cumulus spec 7B role-based) */}
        <CollapsibleSection
          title="Billing"
          icon={<Receipt className="h-4 w-4 text-slate-500" />}
          defaultExpanded={isAdmin && !isPractitioner}
        >
          <div className="space-y-4">
            <PatientInvoicesTab patient={patient} />
            <hr className="border-slate-100" />
            <PatientShipmentsTab
              shipments={recentShipments}
              onViewAll={() => {}}
            />
          </div>
        </CollapsibleSection>

        {/* Section 10: MDI Cases */}
        {isMdiEnabled && patient?.id && (
          <CollapsibleSection
            title="MDI Cases"
            icon={<Stethoscope className="h-4 w-4 text-slate-500" />}
          >
            <PatientMdiCasesTab patientId={patient.id} mdiAuthLink={patient.mdi_auth_link} />
          </CollapsibleSection>
        )}

        {/* Section 11: Tasks */}
        {canViewTasksTab && (
          <CollapsibleSection
            title="Tasks"
            icon={<ClipboardList className="h-4 w-4 text-orange-500" />}
          >
            <TaskPanel providerId={user?.practitionerId || ''} patientId={patient.id} />
          </CollapsibleSection>
        )}

        {/* Section 12: Messaging */}
        <CollapsibleSection
          title="Messaging"
          icon={<MessageSquare className="h-4 w-4 text-violet-500" />}
        >
          <PatientMessagingTab
            patientId={patient.id}
            patientName={patient.full_name || ''}
            patientEmail={patient.email || getDefaultContactValue(patient.contacts, 'email')}
            patientPhone={patient.phone || getDefaultContactValue(patient.contacts, 'phone')}
          />
        </CollapsibleSection>

        {/* Section 13: Activity Timeline — always collapsed */}
        <CollapsibleSection
          title="Activity Timeline"
          icon={<Clock className="h-4 w-4 text-slate-500" />}
        >
          <PatientTimelineTab patientId={patient.id} />
        </CollapsibleSection>

        {/* Section 14: IntelliHealth Full (legacy — collapsed) */}
        {!isGuidedClinic && (
          <CollapsibleSection
            title="IntelliHealth Analysis"
            icon={<Sparkles className="h-4 w-4 text-blue-500" />}
          >
            <IntelliHealthSection patient={patient} />
          </CollapsibleSection>
        )}
      </main>

      {/* ═══════════════════════════════════════════════════
           RIGHT PANEL — IntelliHealth Agent (360px / 40px)
         ═══════════════════════════════════════════════════ */}
      {!isGuidedClinic && (
        <IntelliHealthRightPanel
          patientId={patient.id}
          patientName={patient.full_name || null}
          collapsed={rightPanelCollapsed}
          onToggleCollapse={() => setRightPanelCollapsed((v) => !v)}
          medicationAllergies={patient.medication_allergies ?? null}
          onPrescribe={() => setIsPrescriptionDrawerOpen(true)}
          onOrderLab={() => navigate(`${location.pathname}?tab=labs`)}
          onSchedule={() => setIsAppointmentSheetOpen(true)}
        />
      )}

      {/* ═══════════════════════════════════════════════════
           MODALS & SHEETS (same as PatientDetail.tsx)
         ═══════════════════════════════════════════════════ */}
      {isEditDrawerOpen && (
        <PatientFormDrawer
          open={isEditDrawerOpen}
          onOpenChange={setIsEditDrawerOpen}
          patient={patient}
          onSubmit={handleEditPatient}
          assignedPractitioner={patient.practitioner_id}
          assignedClinic={patient.clinic_id}
        />
      )}

      {isGuidedPickerOpen && patient.id && patient.clinic_id && user?.practitionerId && (
        <GuidedOrderPickerDialog
          open={isGuidedPickerOpen}
          onOpenChange={setIsGuidedPickerOpen}
          patientId={patient.id}
          patientClinicId={patient.clinic_id}
          myPractitionerId={user.practitionerId}
          onSelect={(orderId) => {
            setPendingGuidedOrderId(orderId);
            setIsGuidedPickerOpen(false);
            setIsPrescriptionDrawerOpen(true);
          }}
          onViewAll={() => {}}
        />
      )}

      {isPrescriptionDrawerOpen && (
        <PrescriptionFormSheet
          open={isPrescriptionDrawerOpen}
          onOpenChange={(next) => {
            setIsPrescriptionDrawerOpen(next);
            if (!next) setPendingGuidedOrderId(null);
          }}
          patientId={patient.id}
          practitionerId={user?.practitionerId}
          mode="create"
          onSubmit={() => {}}
          guidedOrderId={pendingGuidedOrderId ?? undefined}
        />
      )}

      {isNoteDrawerOpen && patient.id && (
        <NoteFormDrawer
          open={isNoteDrawerOpen}
          onOpenChange={setIsNoteDrawerOpen}
          patientId={patient.id}
          onSubmit={handleCreateNote}
          note={null}
        />
      )}

      {soapNotesSheetOpen && patient.id && (
        <SOAPNotesListSheet
          open={soapNotesSheetOpen}
          onOpenChange={setSoapNotesSheetOpen}
          patientId={patient.id}
        />
      )}

      {statusDialogOpen && patient && (
        <UpdatePatientStatusDialog
          open={statusDialogOpen}
          onOpenChange={setStatusDialogOpen}
          patient={patient}
          newStatus={pendingStatus}
          onSuccess={handleStatusUpdateSuccess}
        />
      )}

      {isAppointmentSheetOpen && clinic?.id && patient.id && (
        <AppointmentWizard
          open={isAppointmentSheetOpen}
          onOpenChange={setIsAppointmentSheetOpen}
          clinicId={clinic.id}
          defaultPatientId={patient.id}
          onSave={() => {
            setIsAppointmentSheetOpen(false);
            queryClient.invalidateQueries({
              queryKey: patientAppointmentsQueryKey(clinic.id, patient.id),
            });
            toast({ title: 'Success', description: 'Appointment scheduled', variant: 'success' });
          }}
        />
      )}

      {!isGuidedClinic && isBillPatientDialogOpen && patient.id && (
        <BillPatientDialog
          open={isBillPatientDialogOpen}
          onOpenChange={setIsBillPatientDialogOpen}
          patientId={patient.id}
          patientName={patient.full_name}
          onSuccess={() => {
            queryClient.invalidateQueries({
              queryKey: ['patient-invoices', clinic?.id, patient.id],
            });
          }}
        />
      )}
    </div>
  );
};

export default PatientChart;
